import React, { useContext, useEffect, useRef, useState } from "react";
import { Modal, notification, Button } from "antd";
import { ClockCircleOutlined, LogoutOutlined } from "@ant-design/icons";
import { AuthContext } from "../context/AuthContext";

/**
 * SessionExpiryManager
 * --------------------
 * Watches the logged-in admin's JWT (`admin_token`) and, purely from the
 * token's own `exp` claim (no polling, no extra API calls):
 *   1. Shows a one-time "session about to expire" heads-up a few minutes
 *      before the token dies, and
 *   2. Shows a blocking "session expired, please log in again" modal the
 *      moment it dies — which is also the point where every API call would
 *      start 401-ing.
 *
 * There is no refresh/renew endpoint (see constants/apiPath.js), so the
 * session genuinely cannot be extended in place — both prompts route the
 * admin back to a fresh login via AuthContext.logout().
 */

// How long before the actual expiry the heads-up warning appears.
const WARNING_BEFORE_MS = 5 * 60 * 1000; // 5 minutes
const NOTIF_KEY = "session-expiry-warning";

// Decode the `exp` claim (seconds → ms epoch) from the stored JWT without
// verifying the signature — we only need the timestamp, the server still
// enforces validity on every request.
const getTokenExpMs = () => {
  const token = localStorage.getItem("admin_token");
  if (!token) return null;
  try {
    const payload = JSON.parse(
      atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))
    );
    return payload && payload.exp ? payload.exp * 1000 : null;
  } catch (e) {
    return null;
  }
};

const SessionExpiryManager = () => {
  const { isLoggedIn, logout } = useContext(AuthContext);
  // AuthContext re-creates `logout` on every render, so keep it in a ref to
  // avoid it churning the scheduling effect below.
  const logoutRef = useRef(logout);
  logoutRef.current = logout;

  const [expired, setExpired] = useState(false);
  const timers = useRef([]);

  useEffect(() => {
    const clear = () => {
      timers.current.forEach(clearTimeout);
      timers.current = [];
    };

    // Reset on every (re)evaluation — login, logout, token change.
    clear();
    setExpired(false);
    notification.close(NOTIF_KEY);

    if (!isLoggedIn) return clear;

    const expMs = getTokenExpMs();
    if (!expMs) return clear;

    const doExpire = () => {
      notification.close(NOTIF_KEY);
      setExpired(true);
    };

    const doWarn = () => {
      const mins = Math.max(1, Math.round((expMs - Date.now()) / 60000));
      notification.warning({
        key: NOTIF_KEY,
        message: "Session about to expire",
        description: `Your session will expire in about ${mins} minute${
          mins > 1 ? "s" : ""
        }. Please save your work — you'll need to sign in again to continue.`,
        icon: <ClockCircleOutlined style={{ color: "#d97706" }} />,
        duration: 0,
        placement: "topRight",
        btn: (
          <Button
            type="primary"
            size="small"
            onClick={() => {
              notification.close(NOTIF_KEY);
              logoutRef.current();
            }}
          >
            Sign in again
          </Button>
        ),
      });
    };

    const now = Date.now();

    // Already past expiry (e.g. laptop was asleep) — go straight to the modal.
    if (expMs <= now) {
      doExpire();
      return clear;
    }

    // Schedule the hard expiry.
    timers.current.push(setTimeout(doExpire, expMs - now));

    // Schedule (or fire immediately, if we're already inside the window) the
    // heads-up warning.
    const warnDelay = expMs - WARNING_BEFORE_MS - now;
    if (warnDelay <= 0) {
      doWarn();
    } else {
      timers.current.push(setTimeout(doWarn, warnDelay));
    }

    return clear;
  }, [isLoggedIn]);

  return (
    <Modal
      open={expired}
      closable={false}
      maskClosable={false}
      keyboard={false}
      centered
      footer={null}
      width={420}
      className="session-expired-modal"
    >
      <div className="session-expired-body">
        <div className="session-expired-icon">
          <LogoutOutlined />
        </div>
        <h3 className="session-expired-title">Session Expired</h3>
        <p className="session-expired-text">
          Your session has expired for security reasons. Please log in again to
          continue.
        </p>
        <Button
          type="primary"
          block
          size="large"
          className="session-expired-btn"
          onClick={() => logoutRef.current()}
        >
          Log In Again
        </Button>
      </div>
    </Modal>
  );
};

export default SessionExpiryManager;
